// Body work: which picture to show for a truck / trailer, and STARTING hours for body and paint operations per part.
// These are rough starting numbers (not from a published guide). Every line they make is a normal labor line —
// the shop changes the hours on the line when the job is different.

export type BodyModel = { id: string; name: string; trailer?: boolean };
export const BODY_MODELS: BodyModel[] = [
  { id: 'cascadia-new', name: 'Freightliner Cascadia (2018+)' },
  { id: 'cascadia-old', name: 'Freightliner Cascadia (2008–2017)' },
  { id: 'volvo', name: 'Volvo VNL (2018+)' },
  { id: 'volvo-old', name: 'Volvo VNL (2004–2017)' },
  { id: 'kenworth', name: 'Kenworth T680' },
  { id: 'international', name: 'International LT / ProStar' },
  { id: 'peterbilt-579', name: 'Peterbilt 579' },
  { id: 'peterbilt-389', name: 'Peterbilt 389 / 379 / long hood' },
  { id: 'western-star', name: 'Western Star 57X / 5700' },
  { id: 'dry-van', name: 'Dry van trailer', trailer: true },
  { id: 'reefer', name: 'Reefer trailer', trailer: true },
  { id: 'step-deck', name: 'Step deck / flatbed trailer', trailer: true },
];

/** The picture that fits a unit (make / model / year / type). Anything unknown gets the closest common truck. */
export function pickBodyModel(u: { make?: string; model?: string; year?: string | number; type?: string; trailer?: boolean }) {
  const make = String(u.make || '').toLowerCase(), model = String(u.model || '').toLowerCase(), type = String(u.type || '').toLowerCase();
  const year = Number(u.year || 0);
  if (u.trailer || /van|reefer|trailer|flatbed|step|drop|lowboy|tank|dump|chassis/.test(type) && !/truck|tractor|cab/.test(type)) return /reefer/.test(type) ? 'reefer' : /flat|step|drop|lowboy/.test(type) ? 'step-deck' : 'dry-van';
  if (/peterbilt/.test(make)) return /3[5-8]9|long/.test(model) ? 'peterbilt-389' : 'peterbilt-579';
  if (/kenworth/.test(make)) return /w9|w99|t8|long/.test(model) ? 'peterbilt-389' : 'kenworth';
  if (/volvo/.test(make)) return year && year < 2018 ? 'volvo-old' : 'volvo';
  if (/international|navistar/.test(make)) return 'international';
  if (/western\s*star/.test(make)) return 'western-star';
  if (/freightliner|mack/.test(make)) return year && year < 2018 ? 'cascadia-old' : 'cascadia-new';
  return 'cascadia-new';
}

export type BodyOps = { ri?: boolean; rr?: boolean; transfer?: boolean; repair?: '' | 'light' | 'medium' | 'heavy'; refinish?: boolean; blend?: boolean };
type Hours = { rr: number; ri?: number; transfer?: number; refinish?: number; repair?: boolean };

// Hours in hundredths (250 = 2.5 h). First match wins.
const GUIDE: [RegExp, Hours][] = [
  // Names that also contain "window", "door" or "hood" come first.
  [/hinge|lock bar/i, { rr: 75 }],
  [/reefer/i, { rr: 200 }],
  [/cowl lights/i, { rr: 50 }],
  [/hood mirror/i, { rr: 50 }],
  [/mirror/i, { rr: 50 }],
  [/cowl/i, { rr: 150, refinish: 150, repair: true }],
  [/air lines|gladhand/i, { rr: 75 }],
  [/coupler|kingpin/i, { rr: 400 }],
  [/suspension/i, { rr: 300 }],
  [/floor boards/i, { rr: 200, repair: true }],
  [/gooseneck/i, { rr: 400, refinish: 200, repair: true }],
  [/door glass|window|windshield/i, { rr: 150 }],
  [/door handle/i, { rr: 50 }],
  [/storage door|access door/i, { rr: 150, ri: 75, refinish: 150, repair: true }],
  [/rear door/i, { rr: 300, ri: 150, refinish: 300, repair: true }],
  [/\bdoor\b/i, { rr: 250, ri: 150, transfer: 200, refinish: 320, repair: true }],
  [/hood louver|vent/i, { rr: 30 }],
  [/hood/i, { rr: 300, ri: 200, refinish: 450, repair: true }],
  [/a-pillar/i, { rr: 75, refinish: 75 }],
  [/roof fairing side window/i, { rr: 75 }],
  [/visor/i, { rr: 100, refinish: 100, repair: true }],
  [/roof fairing|roof cap|sleeper roof/i, { rr: 300, ri: 150, refinish: 400, repair: true }],
  [/sleeper side panel|cab side/i, { rr: 600, refinish: 350, repair: true }],
  [/sleeper rear wall/i, { rr: 500, refinish: 350, repair: true }],
  [/corner cap|sleeper rear corner/i, { rr: 150, refinish: 150, repair: true }],
  [/cab extender/i, { rr: 75, refinish: 100, repair: true }],
  [/side fairing|skirt|tank cover|step \/|battery box/i, { rr: 100, ri: 50, refinish: 150, repair: true }],
  [/step tread/i, { rr: 40 }],
  [/bumper end|end cap/i, { rr: 75, refinish: 100, repair: true }],
  [/bumper|impact guard/i, { rr: 150, refinish: 200, repair: true }],
  [/grille/i, { rr: 75 }],
  [/headlight|fog|marker|tail light|lights/i, { rr: 50 }],
  [/flare|splash|mud flap/i, { rr: 50 }],
  [/quarter fender|front fender|fender/i, { rr: 150, refinish: 200, repair: true }],
  [/drive axle|tires/i, { rr: 100 }],
  [/fifth wheel/i, { rr: 300 }],
  [/fuel tank/i, { rr: 200 }],
  [/exhaust|air cleaner/i, { rr: 100 }],
  [/landing gear/i, { rr: 300 }],
  [/side panel —|front wall|nose/i, { rr: 400, refinish: 300, repair: true }],
  [/rail|corner post|header|sill/i, { rr: 250, repair: true }],
  [/reefer/i, { rr: 200 }],
  [/hinge|lock bar/i, { rr: 75 }],
  [/tape/i, { rr: 50 }],
];
const REPAIR = { light: 100, medium: 250, heavy: 450 } as const;

export function bodyHours(part: string): Hours {
  return (GUIDE.find(([re]) => re.test(part)) || [null, { rr: 100, refinish: 150, repair: true }])[1];
}

/** What gets ticked when a part is first tapped: painted panels → repair (medium) + refinish; everything else → replace. */
export function defaultOps(part: string): BodyOps {
  const h = bodyHours(part);
  return h.refinish ? { repair: 'medium', refinish: true } : { rr: true };
}

export type BodyLine = { description: string; hours: number; paint: boolean };
/** The labor lines a picked part makes. Paint hours drive paint & materials. */
export function bodyLines(part: string, ops: BodyOps): BodyLine[] {
  const h = bodyHours(part), out: BodyLine[] = [];
  if (ops.rr) out.push({ description: `${part} — replace (R&R)`, hours: h.rr, paint: false });
  else if (ops.ri && h.ri) out.push({ description: `${part} — remove & reinstall (R&I)`, hours: h.ri, paint: false });
  if (ops.rr && ops.transfer && h.transfer) out.push({ description: `${part} — transfer components (glass, regulator, handle, lock, trim)`, hours: h.transfer, paint: false });
  if (!ops.rr && ops.repair && h.repair) out.push({ description: `${part} — repair (${ops.repair} damage)`, hours: REPAIR[ops.repair], paint: false });
  if (ops.refinish && h.refinish) out.push({ description: `${part} — refinish (sand, prime, seal, base, clear)`, hours: h.refinish, paint: true });
  if (ops.blend && h.refinish) out.push({ description: `${part} — blend next panel`, hours: Math.round(h.refinish * 0.4), paint: true });
  return out;
}

/** Which operations a part offers in the picker. */
export function bodyOpsFor(part: string) {
  const h = bodyHours(part);
  return { ri: !!h.ri, transfer: !!h.transfer, repair: !!h.repair, refinish: !!h.refinish };
}

// Part numbers the shop has confirmed for a model (more get added as they are learned).
const PART_NUMBERS: Record<string, [RegExp, string][]> = {
  'cascadia-new': [[/cowl/i, 'A18-64907-003 (check side)']],
};
export function partNumberFor(model: string, part: string) {
  return (PART_NUMBERS[model] || []).find(([re]) => re.test(part))?.[1] || '';
}
